import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { useAuth } from '@/store/auth';
import {
  FileText, Upload, Trash2, Calendar, Building2, Clock, AlertTriangle,
  CheckCircle2, XCircle, Gavel, FileCheck, FileX, MessageSquare,
} from 'lucide-react';

interface Evento {
  id: number;
  lista_id: number;
  tipo: string;
  fecha: string;
  titulo: string;
  descripcion: string | null;
  n_resolucion: string | null;
  organismo: string | null;
  plazo_dias_habiles: number | null;
  fecha_limite_subsanacion: string | null;
  dias_habiles_restantes: number | null;
  archivo_url: string | null;
  archivo_nombre: string | null;
  estado_lista_resultante: string | null;
  created_at: string | null;
}

interface ExpedienteData {
  lista: {
    id: number;
    estado_jee: string;
    n_expediente_jee: string | null;
    fecha_presentacion_jee: string | null;
    jee_competente: string | null;
  };
  obs_vigente: Evento | null;
  eventos: Evento[];
}

const TIPO_META: Record<string, { label: string; icon: any; cls: string }> = {
  presentacion:    { label: 'Presentación', icon: FileText,      cls: 'bg-blue-100 text-blue-800' },
  admision:        { label: 'Admisión',     icon: CheckCircle2,  cls: 'bg-emerald-100 text-emerald-800' },
  observacion:     { label: 'Observación',  icon: AlertTriangle, cls: 'bg-amber-100 text-amber-800' },
  subsanacion:     { label: 'Subsanación',  icon: FileCheck,     cls: 'bg-violet-100 text-violet-800' },
  inscripcion:     { label: 'Inscripción',  icon: CheckCircle2,  cls: 'bg-green-100 text-green-900 font-bold' },
  denegacion:      { label: 'Denegación',   icon: XCircle,       cls: 'bg-red-100 text-red-800' },
  apelacion_arpp:  { label: 'Apelación',    icon: Gavel,         cls: 'bg-orange-100 text-orange-800' },
  resolucion_jne:  { label: 'Resol. JNE',   icon: FileX,         cls: 'bg-indigo-100 text-indigo-800' },
  nota_interna:    { label: 'Nota interna', icon: MessageSquare, cls: 'bg-gray-100 text-gray-700' },
};

const ESTADO_PILL: Record<string, string> = {
  no_presentado: 'bg-gray-200 text-gray-800',
  presentado:    'bg-blue-100 text-blue-800',
  admitido:      'bg-emerald-100 text-emerald-800',
  observado:     'bg-amber-100 text-amber-800',
  subsanado:     'bg-violet-100 text-violet-800',
  inscrito:      'bg-green-200 text-green-900 font-semibold',
  denegado:      'bg-red-100 text-red-800',
  apelado:       'bg-orange-100 text-orange-800',
};

function NuevoEventoForm({ listaId, onSaved }: { listaId: number; onSaved: () => void }) {
  const qc = useQueryClient();
  const [tipo, setTipo] = useState('presentacion');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [nRes, setNRes] = useState('');
  const [organismo, setOrganismo] = useState('JEE');
  const [plazo, setPlazo] = useState(3);
  const [aplicar, setAplicar] = useState(true);

  const mut = useMutation({
    mutationFn: async () =>
      (await api.post(`/expediente-jee/listas/${listaId}/eventos`, {
        tipo, fecha, titulo, descripcion: descripcion || null,
        n_resolucion: nRes || null,
        organismo: organismo || null,
        plazo_dias_habiles: tipo === 'observacion' ? plazo : null,
        aplicar_transicion: aplicar,
      })).data,
    onSuccess: () => {
      setTitulo(''); setDescripcion(''); setNRes('');
      qc.invalidateQueries({ queryKey: ['expediente', listaId] });
      qc.invalidateQueries({ queryKey: ['lista', listaId] });
      onSaved();
    },
  });

  return (
    <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
      <div className="text-xs font-semibold text-blue-900">+ Registrar nuevo evento</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <div>
          <label className="block text-gray-600">Tipo</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full border rounded px-2 py-1">
            {Object.entries(TIPO_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-gray-600">Fecha</label>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-gray-600">Organismo</label>
          <select value={organismo} onChange={(e) => setOrganismo(e.target.value)} className="w-full border rounded px-2 py-1">
            <option value="JEE">JEE</option>
            <option value="JNE">JNE</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <div>
          <label className="block text-gray-600">Título</label>
          <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)}
                 placeholder="Ej: Resol. 0001-2026-JEE-LIMA-CENTRO"
                 className="w-full border rounded px-2 py-1" />
        </div>
        <div>
          <label className="block text-gray-600">N° Resolución / Expediente</label>
          <input type="text" value={nRes} onChange={(e) => setNRes(e.target.value)}
                 placeholder="0001-2026-JEE-XXX"
                 className="w-full border rounded px-2 py-1 font-mono" />
        </div>
      </div>
      <div>
        <label className="block text-gray-600 text-xs">Descripción</label>
        <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                  rows={2} className="w-full border rounded px-2 py-1 text-xs"
                  placeholder="Detalle del evento (qué se observó, qué se subsanó, etc.)" />
      </div>
      {tipo === 'observacion' && (
        <div className="bg-amber-100 border border-amber-300 rounded p-2 text-xs">
          <label className="block text-amber-900 font-medium">
            Plazo de subsanación: <span className="font-mono">{plazo}</span> días hábiles desde {fecha}
          </label>
          <input type="number" min={1} max={10} value={plazo} onChange={(e) => setPlazo(Number(e.target.value))}
                 className="w-20 border rounded px-2 py-1 mt-1" />
        </div>
      )}
      <div className="flex items-center gap-3 text-xs">
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={aplicar} onChange={(e) => setAplicar(e.target.checked)} />
          Auto-actualizar estado de la lista
        </label>
        <button onClick={() => mut.mutate()} disabled={mut.isPending || !titulo.trim()}
                className="ml-auto bg-arpp-primary text-white px-4 py-1.5 rounded disabled:opacity-50">
          {mut.isPending ? 'Guardando…' : 'Guardar evento'}
        </button>
      </div>
      {mut.isError && <div className="text-xs text-red-700">Error al guardar</div>}
    </div>
  );
}

function EventoCard({ ev, onUploaded, puedeBorrar }: { ev: Evento; onUploaded: () => void; puedeBorrar: boolean }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const meta = TIPO_META[ev.tipo] || TIPO_META.nota_interna;
  const Icon = meta.icon;

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return (await api.post(`/expediente-jee/listas/${ev.lista_id}/eventos/${ev.id}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })).data;
    },
    onSuccess: () => onUploaded(),
  });

  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/expediente-jee/eventos/${ev.id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expediente', ev.lista_id] });
      qc.invalidateQueries({ queryKey: ['lista', ev.lista_id] });
    },
  });

  const descargar = async () => {
    const r = await api.get(`/expediente-jee/eventos/${ev.id}/file`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = ev.archivo_nombre ?? `evento_${ev.id}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const dr = ev.dias_habiles_restantes;
  const urgencia = ev.tipo === 'observacion' && dr !== null
    ? (dr <= 0 ? '🔴 VENCIDO' : dr <= 1 ? '🔴' : dr <= 2 ? '🟠' : '🟡')
    : null;

  return (
    <div className="border rounded p-3 bg-white">
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${meta.cls}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
            {ev.organismo && (
              <span className="text-[10px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <Building2 size={10} /> {ev.organismo}
              </span>
            )}
            <span className="text-[11px] text-gray-500 inline-flex items-center gap-1">
              <Calendar size={11} /> {ev.fecha}
            </span>
            {ev.estado_lista_resultante && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${ESTADO_PILL[ev.estado_lista_resultante] || 'bg-gray-100'}`}>
                → {ev.estado_lista_resultante}
              </span>
            )}
          </div>
          <div className="font-medium text-sm mt-1">{ev.titulo}</div>
          {ev.n_resolucion && (
            <div className="text-xs text-gray-600 font-mono">Res/Exp: {ev.n_resolucion}</div>
          )}
          {ev.descripcion && (
            <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{ev.descripcion}</div>
          )}
          {ev.tipo === 'observacion' && ev.fecha_limite_subsanacion && (
            <div className={`mt-2 text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${
              dr !== null && dr <= 1 ? 'bg-red-100 text-red-800' : dr !== null && dr <= 2 ? 'bg-orange-100 text-orange-800' : 'bg-amber-100 text-amber-800'
            }`}>
              <Clock size={12} /> {urgencia} Plazo subsanación hasta {ev.fecha_limite_subsanacion}
              {dr !== null && ` · ${dr} día(s) hábil(es) restante(s)`}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {ev.archivo_url && (
              <button onClick={descargar}
                      className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded inline-flex items-center gap-1 hover:bg-emerald-100">
                <FileText size={11} /> {ev.archivo_nombre ?? 'PDF'}
              </button>
            )}
            <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                   ref={fileRef} className="hidden"
                   onChange={(e) => {
                     const f = e.target.files?.[0];
                     if (f) uploadMut.mutate(f);
                     e.target.value = '';
                   }} />
            <button onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}
                    className="text-[10px] bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-0.5 rounded inline-flex items-center gap-1 disabled:opacity-50">
              <Upload size={11} /> {ev.archivo_url ? 'Reemplazar' : 'Adjuntar'} PDF
            </button>
            {puedeBorrar && (
              <button onClick={() => { if (confirm('Eliminar este evento?')) delMut.mutate(); }}
                      className="text-[10px] text-red-700 hover:bg-red-50 px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Trash2 size={11} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ExpedienteJeeTab({ listaId }: { listaId: number }) {
  const { rol } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const puedeEditar = ['PLT', 'PLA', 'ADM', 'ASI'].includes(rol ?? '');
  const puedeBorrar = ['PLT', 'PLA', 'ADM'].includes(rol ?? '');

  const { data, isLoading } = useQuery<ExpedienteData>({
    queryKey: ['expediente', listaId],
    queryFn: async () => (await api.get(`/expediente-jee/listas/${listaId}`)).data,
  });

  if (isLoading) return <div className="bg-white border rounded p-5 text-sm text-gray-500">Cargando expediente…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-gray-500 uppercase">Estado expediente JEE</div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-3 py-1.5 rounded text-sm font-medium ${ESTADO_PILL[data.lista.estado_jee] || 'bg-gray-100'}`}>
                {data.lista.estado_jee.toUpperCase()}
              </span>
              {data.lista.n_expediente_jee && (
                <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                  Exp: {data.lista.n_expediente_jee}
                </span>
              )}
              {data.lista.fecha_presentacion_jee && (
                <span className="text-xs text-gray-600">
                  Presentación: {data.lista.fecha_presentacion_jee}
                </span>
              )}
              {data.lista.jee_competente && (
                <span className="text-xs text-gray-600">JEE: {data.lista.jee_competente}</span>
              )}
            </div>
          </div>
          {puedeEditar && (
            <button onClick={() => setShowForm((v) => !v)}
                    className="bg-arpp-primary text-white text-xs px-3 py-1.5 rounded">
              {showForm ? '✕ Cancelar' : '+ Nuevo evento'}
            </button>
          )}
        </div>

        {data.obs_vigente && (
          <div className="mt-3 bg-amber-50 border border-amber-300 rounded p-2 text-xs">
            <div className="flex items-center gap-2 font-medium text-amber-900">
              <AlertTriangle size={14} /> Observación vigente
            </div>
            <div className="mt-1">{data.obs_vigente.titulo}</div>
            <div className="text-amber-800">
              Plazo límite: <span className="font-mono">{data.obs_vigente.fecha_limite_subsanacion}</span>
              {data.obs_vigente.dias_habiles_restantes !== null &&
                ` · ${data.obs_vigente.dias_habiles_restantes} día(s) hábil(es) restante(s)`}
            </div>
          </div>
        )}
      </div>

      {showForm && puedeEditar && (
        <NuevoEventoForm listaId={listaId} onSaved={() => setShowForm(false)} />
      )}

      {data.eventos.length === 0 ? (
        <div className="bg-white border rounded p-8 text-sm text-gray-500 italic text-center">
          Sin eventos registrados. {puedeEditar && 'Empieza registrando la presentación al JEE.'}
        </div>
      ) : (
        <div className="space-y-2">
          {data.eventos.map((ev) => (
            <EventoCard key={ev.id} ev={ev}
                        onUploaded={() => {
                          // re-fetch via query invalidation by parent — handled in mutation
                        }}
                        puedeBorrar={puedeBorrar} />
          ))}
        </div>
      )}
    </div>
  );
}
